import React from 'react';
import { Checkbox } from '@frontend/shadcn/components/ui/checkbox';
import {
  CommandSelect,
  CommandSelectTrigger,
  CommandSelectValue,
  CommandSelectContent,
  CommandSelectEmpty,
  CommandSelectLoading,
  CommandSelectItem,
  CommandSelectAll,
  useCommandSelectSearch,
} from '../../Analytics/components/filters-form/command-select';

// Auto-select keywords for campaigns and ad sets
const DEFAULT_AUTO_SELECT_KEYWORDS = ['UGC', 'INFLUENCER', 'ASPIRE'] as const;

// Types for Ad Campaigns and Ad Sets
interface AdCampaign {
  id: string;
  name: string;
  adSets?: AdSet[];
  hasPaidPartnershipAds?: boolean;
}

interface AdSet {
  id: string;
  name: string;
  hasPaidPartnershipAds?: boolean;
}

// Combined type for display

interface AdCampaignsAndSetsSelectProps {
  children: React.ReactNode;
}

export const AdCampaignsAndSetsSelect: React.FC<AdCampaignsAndSetsSelectProps> = ({ children }) => {
  return <div className="relative">{children}</div>;
};

// Helper function to check if campaign/set should be auto-selected
const shouldAutoSelect = (name: string, keywords: readonly string[] = DEFAULT_AUTO_SELECT_KEYWORDS): boolean => {
  const nameUpper = name.toUpperCase();
  return keywords.some((keyword) => nameUpper.includes(keyword));
};

// Helper function to get default selected items (only ad sets now)
export const getDefaultSelectedCampaignsAndSets = (
  adCampaigns: AdCampaign[],
  autoSelectKeywords: readonly string[] = DEFAULT_AUTO_SELECT_KEYWORDS,
): string[] => {
  const defaultSelected: string[] = [];

  adCampaigns.forEach((campaign) => {
    // Check if campaign should be auto-selected (by name patterns or paid partnership ads)
    const campaignNameMatch = shouldAutoSelect(campaign.name, autoSelectKeywords);
    const campaignHasPaidPartnership = campaign.hasPaidPartnershipAds;
    const campaignShouldAutoSelect = campaignNameMatch || campaignHasPaidPartnership;

    if (campaign.adSets) {
      campaign.adSets.forEach((adSet) => {
        // Auto-select ad set if:
        // 1. The ad set name matches patterns, OR
        // 2. The parent campaign name matches patterns, OR
        // 3. The ad set has paid partnership ads, OR
        // 4. The parent campaign has paid partnership ads
        const adSetNameMatch = shouldAutoSelect(adSet.name, autoSelectKeywords);
        const adSetHasPaidPartnership = adSet.hasPaidPartnershipAds;

        if (adSetNameMatch || campaignShouldAutoSelect || adSetHasPaidPartnership) {
          defaultSelected.push(adSet.id);
        }
      });
    }
  });

  return defaultSelected;
};

// Custom Campaign Group Header with Checkbox
const CampaignGroupHeader: React.FC<{
  campaign: AdCampaign;
  selectedIds: string[];
  onToggleCampaign: (campaignId: string) => void;
  isUpdating: boolean;
  autoSelectKeywords: readonly string[];
}> = ({ campaign, selectedIds, onToggleCampaign, isUpdating, autoSelectKeywords }) => {
  const adSetIds = campaign.adSets?.map((adSet) => adSet.id) || [];
  const selectedAdSetIds = selectedIds.filter((id) => adSetIds.includes(id));

  const isAllSelected = adSetIds.length > 0 && selectedAdSetIds.length === adSetIds.length;

  return (
    <div className="flex items-center gap-2 px-2 py-2 text-sm font-medium text-foreground border-b">
      <Checkbox
        checked={isAllSelected}
        onCheckedChange={() => onToggleCampaign(campaign.id)}
        disabled={isUpdating || adSetIds.length === 0}
        aria-label={`Select all ad sets in ${campaign.name} campaign`}
      />
      <span className="flex-1">{campaign.name}</span>
      {(shouldAutoSelect(campaign.name, autoSelectKeywords) || campaign.hasPaidPartnershipAds) && (
        <span className="text-xs text-muted-foreground">
          ⭐ Auto-selected{campaign.hasPaidPartnershipAds ? ' (Paid Partnership)' : ''}
        </span>
      )}
      <span className="text-xs text-muted-foreground">
        ({selectedAdSetIds.length}/{adSetIds.length})
      </span>
    </div>
  );
};

// Content component that can access CommandSelect context
const AdCampaignsAndSetsContent: React.FC<{
  adCampaigns: AdCampaign[];
  isLoading: boolean;
  selectedIds: string[];
  isUpdating: boolean;
  handleSelectItem: (itemId: string) => void;
  updateSelection: (selection: string[]) => void;
  autoSelectKeywords: readonly string[];
}> = ({ adCampaigns, isLoading, selectedIds, isUpdating, handleSelectItem, updateSelection, autoSelectKeywords }) => {
  // Get search query for filtering (now inside CommandSelect context)
  const searchQuery = useCommandSelectSearch();

  // Async filtering to allow UI to show loading state and avoid long main-thread blocks
  const [isFiltering, setIsFiltering] = React.useState(false);
  const [filteredCampaigns, setFilteredCampaigns] = React.useState<AdCampaign[]>([]);
  const filterTimerRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    const queryRaw = searchQuery || '';
    const query = queryRaw.toLowerCase().trim();

    // Clear any pending filter work
    if (filterTimerRef.current) {
      clearTimeout(filterTimerRef.current);
      filterTimerRef.current = null;
    }

    // Empty search → no options
    if (query.length === 0) {
      setIsFiltering(false);
      setFilteredCampaigns([]);
      return;
    }

    // Show loading, then perform filter on next tick to give React a chance to paint
    setIsFiltering(true);
    filterTimerRef.current = window.setTimeout(() => {
      const results = adCampaigns
        .map((campaign) => ({
          ...campaign,
          adSets: (campaign.adSets || []).filter((adSet) => adSet.name.toLowerCase().includes(query)),
        }))
        .filter((campaign) => campaign.name.toLowerCase().includes(query) || (campaign.adSets?.length || 0) > 0);
      setFilteredCampaigns(results);
      setIsFiltering(false);
    }, 0);

    return () => {
      if (filterTimerRef.current) {
        clearTimeout(filterTimerRef.current);
        filterTimerRef.current = null;
      }
    };
  }, [adCampaigns, searchQuery]);

  // Get all visible ad set IDs (only ad sets are selectable now)
  const allVisibleIds = React.useMemo(() => {
    const ids: string[] = [];
    filteredCampaigns.forEach((campaign) => {
      if (campaign.adSets) {
        campaign.adSets.forEach((adSet) => {
          ids.push(adSet.id);
        });
      }
    });
    return ids;
  }, [filteredCampaigns]);

  // Use refs to store latest values for stable callbacks
  const selectedIdsRef = React.useRef(selectedIds);
  const allVisibleIdsRef = React.useRef(allVisibleIds);

  // Update refs when values change
  React.useEffect(() => {
    selectedIdsRef.current = selectedIds;
  }, [selectedIds]);

  React.useEffect(() => {
    allVisibleIdsRef.current = allVisibleIds;
  }, [allVisibleIds]);

  // Handle campaign checkbox toggle (selects/deselects all ad sets in campaign)
  const handleToggleCampaign = React.useCallback(
    (campaignId: string) => {
      if (isUpdating) return;

      const campaign = filteredCampaigns.find((c) => c.id === campaignId);
      if (!campaign?.adSets) return;

      const adSetIds = campaign.adSets.map((adSet) => adSet.id);
      const selectedAdSetIds = selectedIds.filter((id) => adSetIds.includes(id));

      let newSelection: string[];

      if (selectedAdSetIds.length === adSetIds.length) {
        // Deselect all ad sets in this campaign
        newSelection = selectedIds.filter((id) => !adSetIds.includes(id));
      } else {
        // Select all ad sets in this campaign
        newSelection = Array.from(new Set([...selectedIds, ...adSetIds]));
      }

      updateSelection(newSelection);
    },
    [filteredCampaigns, selectedIds, updateSelection, isUpdating],
  );

  // Create a wrapped handleSelectAll that works with filtered items
  const wrappedHandleSelectAll = React.useCallback(() => {
    if (isUpdating || isLoading) {
      return;
    }

    // Use refs to get current values without dependencies
    const currentSelectedIds = selectedIdsRef.current;
    const currentAllVisibleIds = allVisibleIdsRef.current;
    const selectedVisibleIds = currentSelectedIds.filter((id) => currentAllVisibleIds.includes(id));

    let newSelection: string[];

    if (selectedVisibleIds.length === currentAllVisibleIds.length && currentAllVisibleIds.length > 0) {
      // Deselect all visible items (preserve selections from other search contexts)
      newSelection = currentSelectedIds.filter((id) => !currentAllVisibleIds.includes(id));
    } else {
      // Select all visible items (preserve existing selections)
      newSelection = Array.from(new Set([...currentSelectedIds, ...currentAllVisibleIds]));
    }

    updateSelection(newSelection);
  }, [updateSelection, isUpdating, isLoading]);

  // Calculate select all state
  const selectAllState = React.useMemo(() => {
    const selectedVisibleIds = selectedIds.filter((id) => allVisibleIds.includes(id));
    return {
      checked: allVisibleIds.length > 0 && selectedVisibleIds.length === allVisibleIds.length,
      indeterminate: selectedVisibleIds.length > 0 && selectedVisibleIds.length < allVisibleIds.length,
    };
  }, [allVisibleIds, selectedIds]);

  return (
    <>
      {isLoading || isFiltering ? (
        <CommandSelectLoading>
          {isFiltering ? 'Searching campaigns and ad sets…' : 'Loading campaigns and ad sets...'}
        </CommandSelectLoading>
      ) : (
        <>
          {/* Select All/Deselect All */}
          {allVisibleIds.length > 0 && (
            <CommandSelectAll
              checked={selectAllState.checked}
              indeterminate={selectAllState.indeterminate}
              disabled={isLoading || isUpdating}
              onToggle={wrappedHandleSelectAll}
              count={allVisibleIds.length}
            />
          )}

          {filteredCampaigns.length > 0 ? (
            filteredCampaigns.map((campaign) => (
              <div key={campaign.id} className="border-b border-border last:border-b-0">
                {/* Campaign Header with Checkbox */}
                <CampaignGroupHeader
                  campaign={campaign}
                  selectedIds={selectedIds}
                  onToggleCampaign={handleToggleCampaign}
                  isUpdating={isUpdating}
                  autoSelectKeywords={autoSelectKeywords}
                />

                {/* Ad Sets under this campaign */}
                {campaign.adSets && campaign.adSets.length > 0 && (
                  <div className="bg-muted/30">
                    {campaign.adSets.map((adSet) => (
                      <CommandSelectItem
                        key={adSet.id}
                        value={adSet.id}
                        onSelect={() => handleSelectItem(adSet.id)}
                        selected={selectedIds.includes(adSet.id)}
                        disabled={isLoading || isUpdating}
                        className="pl-8 border-b-0"
                      >
                        <div className="flex flex-col">
                          <span>{adSet.name}</span>
                          <span className="text-xs text-muted-foreground">
                            Ad Set
                            {(shouldAutoSelect(adSet.name, autoSelectKeywords) || adSet.hasPaidPartnershipAds) &&
                              ` ⭐ Auto-selected${adSet.hasPaidPartnershipAds ? ' (Paid Partnership)' : ''}`}
                          </span>
                        </div>
                      </CommandSelectItem>
                    ))}
                  </div>
                )}
              </div>
            ))
          ) : (
            <>
              {!searchQuery || searchQuery.trim() === '' ? (
                <CommandSelectEmpty>Start typing to search campaigns and ad sets.</CommandSelectEmpty>
              ) : (
                <CommandSelectEmpty>No campaigns or ad sets match your search.</CommandSelectEmpty>
              )}
            </>
          )}
        </>
      )}
    </>
  );
};

export const AdCampaignsAndSetsOptions = ({
  adCampaigns = [],
  isLoading = false,
  value,
  onChange,
  autoSelectKeywords = DEFAULT_AUTO_SELECT_KEYWORDS,
}: {
  adCampaigns?: AdCampaign[];
  isLoading?: boolean;
  value: string[];
  onChange: (ids: string[]) => void;
  autoSelectKeywords?: readonly string[];
}) => {
  // Local state management to prevent infinite loops (same pattern as AdAccountsOptions)
  const [selectedIds, setSelectedIds] = React.useState<string[]>(value || []);
  const [isUpdating, setIsUpdating] = React.useState(false);

  // Helper function for set-based array comparison (order-independent)
  const arraysHaveSameElements = React.useCallback((arr1: string[], arr2: string[]): boolean => {
    if (arr1.length !== arr2.length) return false;
    const set1 = new Set(arr1);
    const set2 = new Set(arr2);
    return set1.size === set2.size && [...set1].every((item) => set2.has(item));
  }, []);

  // Sync selectedIds with value prop (for form initialization and updates)
  React.useEffect(() => {
    if (!isUpdating) {
      // Only update if arrays are actually different (set-based equality check)
      const arraysAreEqual = arraysHaveSameElements(selectedIds, value || []);

      if (!arraysAreEqual) {
        setSelectedIds(value || []);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, isUpdating, arraysHaveSameElements]); // selectedIds intentionally excluded to prevent infinite loop

  // Unified update handler with proper state management
  const updateSelection = React.useCallback(
    (newSelection: string[]) => {
      setIsUpdating(true);
      setSelectedIds(newSelection);
      onChange(newSelection);
    },
    [onChange],
  );

  // Reset isUpdating flag when state synchronization is complete
  // This replaces setTimeout to avoid race conditions and improve testability
  React.useEffect(() => {
    if (isUpdating) {
      // Check if local state matches prop value (state has settled)
      const arraysAreEqual = arraysHaveSameElements(selectedIds, value || []);

      if (arraysAreEqual) {
        setIsUpdating(false);
      }
    }
  }, [isUpdating, selectedIds, value, arraysHaveSameElements]);

  const handleSelectItem = React.useCallback(
    (itemId: string) => {
      if (isUpdating) return;

      const newSelection = selectedIds.includes(itemId)
        ? selectedIds.filter((id) => id !== itemId)
        : [...selectedIds, itemId];

      updateSelection(newSelection);
    },
    [selectedIds, updateSelection, isUpdating],
  );

  const handleDismiss = React.useCallback(
    (e: React.SyntheticEvent) => {
      e.preventDefault();
      e.stopPropagation();
      updateSelection([]);
    },
    [updateSelection],
  );

  // Memoized ad set lookup map for O(1) performance
  const adSetMap = React.useMemo(() => {
    const map = new Map<string, AdSet>();
    adCampaigns.forEach((campaign) => {
      if (campaign.adSets) {
        campaign.adSets.forEach((adSet) => {
          map.set(adSet.id, adSet);
        });
      }
    });
    return map;
  }, [adCampaigns]);

  // Memoized display text calculation for performance optimization
  const displayText = React.useMemo(() => {
    if (selectedIds.length === 0) {
      return { mainText: 'Select campaigns and ad sets...', count: null };
    }

    // Use O(1) map lookup instead of nested loops
    const firstSelectedAdSet = adSetMap.get(selectedIds[0]);

    if (selectedIds.length === 1) {
      return {
        mainText: firstSelectedAdSet?.name || 'Select campaigns and ad sets...',
        count: null,
      };
    }

    // Multiple selections - show first item + count
    return {
      mainText: firstSelectedAdSet?.name || 'Multiple selected',
      count: selectedIds.length - 1,
    };
  }, [selectedIds, adSetMap]);

  return (
    <CommandSelect>
      <CommandSelectTrigger placeholder="Select campaigns and ad sets...">
        <CommandSelectValue
          placeholder="Select campaigns and ad sets..."
          displayText={selectedIds.length > 0 ? displayText.mainText : undefined}
          count={displayText.count}
          onClear={handleDismiss}
          disabled={isUpdating}
        />
      </CommandSelectTrigger>
      <CommandSelectContent searchPlaceholder="Search campaigns and ad sets..." maxHeight="300px">
        <AdCampaignsAndSetsContent
          adCampaigns={adCampaigns}
          isLoading={isLoading}
          selectedIds={selectedIds}
          isUpdating={isUpdating}
          handleSelectItem={handleSelectItem}
          updateSelection={updateSelection}
          autoSelectKeywords={autoSelectKeywords}
        />
      </CommandSelectContent>
    </CommandSelect>
  );
};
